import {
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TablePagination,
  TableRow,
  Theme,
  Tooltip,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useParams } from 'react-router-dom';
import React, { useCallback, useEffect, useState } from 'react';
import ClientsSecreteTableHead from './ClientSecreteTableHead';
import { Order } from '../../../../Helpers/Literals';
import { ClientSecretMetaData } from '../Model/ClientSecretesModel';
import {
  getLocalDate,
  getSorting,
  stableSort,
} from '../../../../Helpers/UtilityMethods';
import ClientsSecreteHeaderToolbar from './ClientsSecreteHeaderToolbar';
import { customerService } from '../../Customer/CustomerService';
import { CustomerV2 } from '../../Customer/Model/CustomerModel';
import { clientsecretService } from '../ClientSecretService';
import ClientsSecreteMenu from '../Components/ClientSecretMenu';
import { Loader } from '../../../../Common/Components/Loader';
import { useTranslation } from 'react-i18next';
import { showToast } from '../../../../lib/toast';

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
    width: '100%',
  },
  tableWrapper: {
    overflowX: 'auto' as const,
  },
}));

interface Param {
  customerId: string;
}

type ClientSecretMetaDataRow = {
  [K in keyof Required<ClientSecretMetaData>]: NonNullable<
    ClientSecretMetaData[K]
  >;
};

export default function ClientSecreteHome() {
  const { t } = useTranslation('clientsecret');
  const params = useParams<Param>();
  const { classes } = useStyles();

  const [customer, setCustomer] = useState<CustomerV2>({});
  const [update, setUpdate] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [updateParent, setUpdateParent] = useState(false);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] =
    useState<keyof ClientSecretMetaData>('ClientId');
  const [clientSecrets, setClientSecrets] = useState<ClientSecretMetaDataRow[]>(
    [],
  );
  const [clientSecretsCopy, setClientSecretsCopy] = useState<
    ClientSecretMetaDataRow[]
  >([]);

  const rowsPerPage = 10;
  const [page, setPage] = useState(0);
  const emptyRows =
    rowsPerPage -
    Math.min(rowsPerPage, clientSecrets.length - page * rowsPerPage);

  const loadCustomerData = useCallback(async () => {
    try {
      const customerData = await customerService.getCustomer(params.customerId);
      setCustomer(customerData);
    } catch (error) {
      console.error(error);
      showToast({ title: t((error as Error).message), type: 'error' });
    }
  }, [params.customerId, t]);

  const loadClientSecretMetaData = useCallback(async () => {
    try {
      const clientSecretMetadata =
        await clientsecretService.getClientSecretMetadata(params.customerId);

      setClientSecrets(
        (clientSecretMetadata.Items ?? []) as ClientSecretMetaDataRow[],
      );
      setClientSecretsCopy(
        (clientSecretMetadata.Items ?? []) as ClientSecretMetaDataRow[],
      );
    } catch (error) {
      console.error(error);
      showToast({ title: t('clientsecret.error.load_failed'), type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [params.customerId, t]);

  const loadInitData = useCallback(async () => {
    setLoading(true);
    await loadCustomerData();
    await loadClientSecretMetaData();
  }, [loadCustomerData, loadClientSecretMetaData]);

  useEffect(() => {
    loadInitData();
  }, [updateParent, update]);

  const updateClientActiveness = async (
    clientId: string,
    isActive: boolean,
  ) => {
    setLoading(true);
    try {
      await clientsecretService.updateClientActiveness(
        params.customerId,
        clientId,
        isActive,
      );
      setUpdate((prev) => !prev);
      const status = isActive ? 'Activated' : 'Deactivated';
      showToast({ title: `Api Key ${clientId} ${status}`, type: 'success' });
    } catch (error) {
      console.error(error);
      showToast({
        title: t('clientsecret.error.update_failed'),
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  function oncloseUpdateParent() {
    setUpdateParent((prev) => !prev);
  }

  const handleFilter = (searchFilterText: string) => {
    const filtered = clientSecretsCopy.filter((tc) =>
      tc.ClientId?.toLowerCase().includes(searchFilterText.toLowerCase()),
    );
    setClientSecrets(filtered);
  };

  function handleSearchFilter(event: React.ChangeEvent<HTMLInputElement>) {
    handleFilter(event.target.value);
  }

  const handleSwitch = async (
    event: React.ChangeEvent<HTMLInputElement>,
    row: ClientSecretMetaData,
  ) => {
    await updateClientActiveness(row.ClientId!, event.target.checked);
  };

  function handleChangePage(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) {
    setPage(newPage);
  }

  function handleRequestSort(
    event: React.MouseEvent<unknown>,
    property: keyof ClientSecretMetaData,
  ) {
    const isDesc = orderBy === property && order === 'desc';
    setOrder(isDesc ? 'asc' : 'desc');
    setOrderBy(property);
  }

  return (
    <div className={classes.root}>
      <ClientsSecreteHeaderToolbar
        onFilterRequest={handleSearchFilter}
        customer={customer}
        oncloseUpdateParent={oncloseUpdateParent}
      />
      <Paper className={classes.paper}>
        {loading ? (
          <Loader loading={loading} />
        ) : (
          <div className={classes.tableWrapper}>
            <Table className={classes.table}>
              <ClientsSecreteTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {stableSort(clientSecrets, getSorting(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((tc) => (
                    <TableRow key={tc.ClientId}>
                      <TableCell align="center">{tc.ClientId}</TableCell>
                      <TableCell align="center">{tc.Description}</TableCell>
                      <TableCell align="center">
                        {getLocalDate(tc.CreatedDate)}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            tc.IsActive
                              ? t('clientsecret.tooltip.deactivate')
                              : t('clientsecret.tooltip.activate')
                          }
                        >
                          <Switch
                            checked={tc.IsActive ?? false}
                            onChange={(evt) => handleSwitch(evt, tc)}
                            name="isActive"
                            color="primary"
                            inputProps={{ 'aria-label': 'toggle active state' }}
                          />
                        </Tooltip>
                        {tc.IsActive ? 'Active' : 'Not Active'}
                      </TableCell>
                      <TableCell>
                        <ClientsSecreteMenu
                          clientId={tc.ClientId!}
                          customerId={params.customerId}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={7} />
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[10]}
                    colSpan={7}
                    count={clientSecrets.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    SelectProps={{
                      inputProps: { 'aria-label': 'Rows per page' },
                      native: true,
                    }}
                    onPageChange={handleChangePage}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        )}
      </Paper>
    </div>
  );
}
